// mixins/update.js - Enhanced version with auto-refresh
export default {
  data() {
    return {
      // Original refresh variables
      refreshing: false,
      registration: null,
      updateExists: false,

      // New tracking variables for auto-refresh
      appHidden: false,
      lastActiveTime: Date.now(),
      inactivityThreshold: 5 * 60 * 1000, // 5 minutes in milliseconds
      inactivityInterval: null
    }
  },
  created() {
    // Listen for our custom event from the SW registration
    document.addEventListener("swUpdated", this.updateAvailable, { once: true })

    // Prevent multiple refreshes
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (this.refreshing) return
      this.refreshing = true
      // Here the actual reload of the page occurs
      window.location.reload()
    })

    // Initialize last active time
    this.lastActiveTime = Date.now()

    // Add visibility change listener
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    // Add user activity listeners
    document.addEventListener('mousemove', this.updateLastActiveTime)
    document.addEventListener('keypress', this.updateLastActiveTime)
    document.addEventListener('click', this.updateLastActiveTime)
    document.addEventListener('touchstart', this.updateLastActiveTime)

    // Check for inactivity and updates periodically
    this.startInactivityCheck()

    // Check if this is a return visit with considerable time gap
    const lastVisitTime = localStorage.getItem("last_visit_time")
    const currentTime = Date.now()

    if (lastVisitTime) {
      const timeSinceLastVisit = currentTime - parseInt(lastVisitTime)
      // If returning after a day or more, force refresh to ensure latest version
      if (timeSinceLastVisit > 24 * 60 * 60 * 1000) {
        window.location.reload(true)
      }
    }

    // Store the current visit time
    localStorage.setItem("last_visit_time", currentTime.toString())
  },
  beforeDestroy() {
    // Clean up event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    document.removeEventListener('mousemove', this.updateLastActiveTime)
    document.removeEventListener('keypress', this.updateLastActiveTime)
    document.removeEventListener('click', this.updateLastActiveTime)
    document.removeEventListener('touchstart', this.updateLastActiveTime)

    if (this.inactivityInterval) {
      clearInterval(this.inactivityInterval)
    }
  },
  methods: {
    // Store the SW registration so we can send it a message
    // We use `updateExists` to control whatever alert, toast, dialog, etc we want to use
    // To alert the user there is an update they need to refresh for
    updateAvailable(event) {
      this.registration = event.detail
      this.updateExists = true
    },

    // Called when the user accepts the update
    refreshApp() {
      this.updateExists = false
      // Make sure we only send a 'skip waiting' message if the SW is waiting
      if (this.registration && this.registration.waiting) {
        // send message to SW to skip the waiting and activate the new SW
        this.registration.waiting.postMessage({ type: "SKIP_WAITING" })
      } else {
        // Fallback: if no registration, just reload the page
        window.location.reload()
      }
    },

    // New methods for auto-refresh functionality
    handleVisibilityChange() {
      const isHidden = document.hidden

      // When the app becomes visible again after being hidden
      if (this.appHidden && !isHidden) {
        const timeAwayMs = Date.now() - this.lastActiveTime

        // If returning after significant time away AND there's an update
        // OR returning after very long absence (refresh anyway for latest version)
        if ((timeAwayMs > this.inactivityThreshold && this.updateExists) ||
          timeAwayMs > 30 * 60 * 1000) { // 30 minutes
          this.autoRefreshApp()
        }
      }

      this.appHidden = isHidden
      if (!isHidden) {
        this.updateLastActiveTime()
      }
    },

    updateLastActiveTime() {
      this.lastActiveTime = Date.now()
    },

    startInactivityCheck() {
      this.inactivityInterval = setInterval(() => {
        const timeInactive = Date.now() - this.lastActiveTime

        // If inactive for threshold time and update exists, consider auto-refresh
        if (timeInactive > this.inactivityThreshold && this.updateExists && !this.appHidden) {
          // If very inactive, just refresh automatically
          if (timeInactive > 15 * 60 * 1000) { // 15 minutes
            this.autoRefreshApp()
          }
          // Otherwise the snackbar will be visible due to updateExists being true
        }
      }, 60000) // Check every minute
    },

    autoRefreshApp() {
      if (this.refreshing) return
      this.refreshing = true

      // Create a brief notification that auto-refreshes
      const notificationEl = document.createElement('div')
      notificationEl.style.position = 'fixed'
      notificationEl.style.bottom = '20px'
      notificationEl.style.right = '20px'
      notificationEl.style.padding = '16px 24px'
      notificationEl.style.background = '#4CAF50'
      notificationEl.style.color = 'white'
      notificationEl.style.borderRadius = '4px'
      notificationEl.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)'
      notificationEl.style.zIndex = '9999'
      notificationEl.style.transition = 'opacity 0.3s'
      notificationEl.textContent = 'Updating to latest version...'

      document.body.appendChild(notificationEl)

      // Refresh after a brief delay to allow notification to be seen
      setTimeout(() => {
        this.refreshApp()
      }, 1500)
    }
  }
}
